"use client";

import { useSearchParams } from "next/navigation";
import { Suspense, useEffect } from "react";

type UTMParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  video_id?: string;
  video_title?: string;
};

const UTM_STORAGE_KEY = "vind_utm_params";
const UTM_EXPIRY_KEY = "vind_utm_expiry";
const UTM_EXPIRY_HOURS = 24; // UTM attribution window

export const UTMTracker = () => {
  return (
    <Suspense fallback={null}>
      <TrackerInner />
    </Suspense>
  );
};

const TrackerInner = () => {
  const searchParams = useSearchParams();

  useEffect(() => {
    // Extract UTM parameters from URL
    const utmParams: UTMParams = {
      utm_source: searchParams.get("utm_source") ?? undefined,
      utm_medium: searchParams.get("utm_medium") ?? undefined,
      utm_campaign: searchParams.get("utm_campaign") ?? undefined,
      utm_term: searchParams.get("utm_term") ?? undefined,
      utm_content: searchParams.get("utm_content") ?? undefined,
      video_id: searchParams.get("video_id") ?? undefined,
      video_title: searchParams.get("video_title") ?? undefined,
    };

    // Check if any UTM parameters are present
    const hasUTMParams = Object.values(utmParams).some(
      (value) => value !== undefined,
    );

    if (hasUTMParams) {
      // Store UTM parameters in localStorage with expiry
      const expiryTime = Date.now() + UTM_EXPIRY_HOURS * 60 * 60 * 1000;
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
      localStorage.setItem(UTM_EXPIRY_KEY, expiryTime.toString());

      // Track the video visit immediately
      if (utmParams.utm_source === "youtube" && utmParams.video_id) {
        trackVideoVisit(utmParams);
      }

      // Send to Google Analytics
      if (window.gtag) {
        window.gtag("event", "utm_capture", {
          event_category: "traffic_source",
          utm_source: utmParams.utm_source,
          utm_medium: utmParams.utm_medium,
          utm_campaign: utmParams.utm_campaign,
          utm_term: utmParams.utm_term,
          utm_content: utmParams.utm_content,
          video_id: utmParams.video_id,
          video_title: utmParams.video_title,
        });
      }

      // Send to Umami
      if (window.umami) {
        window.umami.track("video_traffic_source", {
          source: utmParams.utm_source ?? "unknown",
          medium: utmParams.utm_medium ?? "unknown",
          campaign: utmParams.utm_campaign ?? "unknown",
          video_id: utmParams.video_id ?? "unknown",
          video_title: utmParams.video_title ?? "unknown",
        });
      }

      // Send to Microsoft Clarity (via custom tags)
      if (window.clarity) {
        window.clarity("set", "utm_source", utmParams.utm_source ?? "unknown");
        window.clarity("set", "utm_medium", utmParams.utm_medium ?? "unknown");
        window.clarity(
          "set",
          "utm_campaign",
          utmParams.utm_campaign ?? "unknown",
        );
        window.clarity("set", "video_id", utmParams.video_id ?? "unknown");
      }
    }
  }, [searchParams]);

  return null;
};

// Function to track video visit
const trackVideoVisit = (utmParams: UTMParams) => {
  // Track with Umami
  if (window.umami) {
    window.umami.track("youtube_video_visit", {
      video_id: utmParams.video_id ?? "unknown",
      video_title: utmParams.video_title ?? "unknown",
      campaign: utmParams.utm_campaign ?? "unknown",
      term: utmParams.utm_term ?? "unknown",
      content: utmParams.utm_content ?? "unknown",
    });
  }

  // Track with Google Analytics
  if (window.gtag) {
    window.gtag("event", "video_visit", {
      event_category: "youtube_traffic",
      event_label: utmParams.video_id,
      custom_parameter_1: utmParams.video_title,
      custom_parameter_2: utmParams.utm_campaign,
    });
  }
};

// Utility function to get stored UTM parameters
export const getStoredUTMParams = (): UTMParams | null => {
  if (typeof window === "undefined") return null;

  try {
    const expiryTime = localStorage.getItem(UTM_EXPIRY_KEY);
    if (!expiryTime || Date.now() > parseInt(expiryTime)) {
      // UTM data has expired
      localStorage.removeItem(UTM_STORAGE_KEY);
      localStorage.removeItem(UTM_EXPIRY_KEY);
      return null;
    }

    const storedParams = localStorage.getItem(UTM_STORAGE_KEY);
    return storedParams ? JSON.parse(storedParams) : null;
  } catch (error) {
    console.error("Error reading UTM parameters:", error);
    return null;
  }
};

// Function to include UTM data in conversion tracking
export const trackConversionWithUTM = (
  eventName: string,
  additionalData: Record<string, any> = {},
) => {
  const utmParams = getStoredUTMParams();

  const eventData = {
    ...additionalData,
    utm_source: utmParams?.utm_source,
    utm_medium: utmParams?.utm_medium,
    utm_campaign: utmParams?.utm_campaign,
    utm_term: utmParams?.utm_term,
    utm_content: utmParams?.utm_content,
    video_id: utmParams?.video_id,
    video_title: utmParams?.video_title,
  };

  // Track with Umami (filter out undefined values)
  if (window.umami) {
    const filteredEventData = Object.fromEntries(
      Object.entries(eventData).filter(([_, value]) => value !== undefined),
    ) as Record<string, string | number>;
    window.umami.track(eventName, filteredEventData);
  }

  // Track with Google Analytics (filter out undefined values)
  if (window.gtag) {
    const filteredEventData = Object.fromEntries(
      Object.entries(eventData).filter(([_, value]) => value !== undefined),
    );
    window.gtag("event", eventName, {
      event_category: "conversion",
      ...filteredEventData,
    });
  }

  return eventData;
};
